import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import SafeIcon from '../../common/SafeIcon';

const {
  FiPlay, FiClock, FiEye, FiMapPin, FiCalendar, FiStar,
} = FiIcons;

const { FaYoutube, FaFacebook, FaGlobe } = FaIcons;

const platformIcon = (platform) => {
  switch (platform) {
    case 'youtube':   return FaYoutube;
    case 'facebook':  return FaFacebook;
    default:          return FaGlobe;
  }
};

const platformColor = (platform) => {
  switch (platform) {
    case 'youtube':   return 'text-red-500';
    case 'facebook':  return 'text-blue-600';
    default:          return 'text-accent-gold';
  }
};

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const ReplayCard = ({ replay, idx, onClick }) => {
  const PlatIcon = platformIcon(replay.platform);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ delay: idx * 0.05 }}
      whileHover={{ y: -6 }}
      className="bg-white rounded-[32px] overflow-hidden shadow-xl border border-brand-dark group cursor-pointer"
      onClick={onClick}
    >
      {/* Thumbnail */}
      <div className="relative aspect-video overflow-hidden">
        <img
          src={replay.thumbnail}
          alt={replay.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          onError={(e) => {
            e.target.src = 'https://images.unsplash.com/photo-1465146344425-f00d5f5c8f07?q=80&w=2076&auto=format&fit=crop';
          }}
        />
        {/* Dark overlay on hover */}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center">
          <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center text-white border border-white/30 transform scale-90 group-hover:scale-100 transition-transform">
            <SafeIcon icon={FiPlay} className="w-7 h-7 ml-0.5" />
          </div>
        </div>
        {/* Duration badge */}
        <div className="absolute bottom-3 right-3 bg-black/70 backdrop-blur-sm text-white px-2.5 py-1 rounded-lg text-[10px] font-bold flex items-center gap-1.5">
          <SafeIcon icon={FiClock} className="w-3 h-3" />
          {replay.duration}
        </div>
        {/* Featured star */}
        {replay.featured && (
          <div className="absolute top-3 left-3 bg-accent-gold text-white px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest flex items-center gap-1 shadow-lg">
            <SafeIcon icon={FiStar} className="w-2.5 h-2.5" /> Featured
          </div>
        )}
        {/* Platform badge */}
        <div className="absolute top-3 right-3 bg-black/50 backdrop-blur-md rounded-lg p-1.5 border border-white/10">
          <SafeIcon icon={PlatIcon} className={`w-4 h-4 ${platformColor(replay.platform)}`} />
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Category + date row */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className="px-2.5 py-0.5 bg-brand-light text-accent-navy text-[8px] font-black uppercase tracking-wider rounded-full border border-brand-dark">
            {replay.category}
          </span>
          <span className="text-text-muted text-[10px] font-medium flex items-center gap-1">
            <SafeIcon icon={FiCalendar} className="w-3 h-3" />
            {formatDate(replay.recordedDate)}
          </span>
        </div>

        {/* Title */}
        <h3 className="text-lg font-serif text-accent-navy mb-1.5 group-hover:text-accent-gold transition-colors leading-tight line-clamp-2">
          {replay.title}
        </h3>

        {/* Organizer */}
        <p className="text-text-muted text-[10px] font-bold uppercase tracking-widest mb-4">
          Hosted by {replay.organizer}
        </p>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-brand-light">
          <div className="flex items-center gap-3 text-text-muted text-[10px] font-bold">
            <span className="flex items-center gap-1.5">
              <SafeIcon icon={FiEye} className="w-3.5 h-3.5 text-accent-gold" />
              {replay.views.toLocaleString()}
            </span>
            {replay.location && (
              <span className="flex items-center gap-1.5">
                <SafeIcon icon={FiMapPin} className="w-3.5 h-3.5 text-accent-gold" />
                <span className="truncate max-w-[100px]">{replay.location}</span>
              </span>
            )}
          </div>
          <button className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-accent-navy group-hover:text-accent-gold transition-colors">
            <SafeIcon icon={FiPlay} className="w-3 h-3" />
            Watch
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default ReplayCard;